const MOBILE_MATCHES_APPLICANT_MESSAGE = 'Next of kin cannot be an applicant on this loan.';
const EMAIL_MATCHES_APPLICANT_MESSAGE = 'Next of kin cannot be an applicant on this loan.';
const NEXT_OF_KIN_REQUIRED_MESSAGE = 'Next of kin details are required before this offer can be accepted.';
const GENERIC_API_ERROR_MESSAGE = 'We could not accept your offer. Please check your details and try again.';

/**
 * Normalises an email for comparison. Matches how the API compares the two values.
 *
 * @param {string} [value]
 * @returns {string}
 */
function normalizeEmail(value) {
  return (value || '').trim().toLowerCase();
}

/**
 * Normalises a mobile number to its national form so the same number written different ways still
 * compares equal: applicants are stored in whichever format they were captured in, so `+61 411 111
 * 111` and `0411111111` are the same person. Matches how the API compares the two values.
 *
 * @param {string} [value]
 * @returns {string}
 */
function normalizeMobilePhoneNumber(value) {
  const digits = (value || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('61')) return `0${digits.slice(2)}`;
  return digits;
}

/**
 * Finds the next of kin fields that collide with an applicant on the loan. The next of kin is meant
 * to be a third party the lender can reach independently of the borrower, so sharing a mobile
 * number or email address with anyone on the application is rejected.
 *
 * Every applicant is checked, not just the primary. Blank values never match, so an applicant with
 * no recorded mobile does not block a next of kin who also has none.
 *
 * @param {{ mobilePhoneNumber?: string, email?: string }} nextOfKin
 * @param {Array<{ mobilePhoneNumber?: string, email?: string }>} [applicants]
 * @returns {Record<string, string>} Errors keyed by field name; empty when nothing collides
 */
export function findApplicantContactMatches(nextOfKin, applicants) {
  const errors = {};
  const nextOfKinMobilePhoneNumber = normalizeMobilePhoneNumber(nextOfKin?.mobilePhoneNumber);
  const nextOfKinEmail = normalizeEmail(nextOfKin?.email);
  (applicants || []).forEach((applicant) => {
    if (
      nextOfKinMobilePhoneNumber &&
      nextOfKinMobilePhoneNumber === normalizeMobilePhoneNumber(applicant?.mobilePhoneNumber)
    ) {
      errors.mobilePhoneNumber = MOBILE_MATCHES_APPLICANT_MESSAGE;
    }
    if (nextOfKinEmail && nextOfKinEmail === normalizeEmail(applicant?.email)) {
      errors.email = EMAIL_MATCHES_APPLICANT_MESSAGE;
    }
  });
  return errors;
}

/**
 * Maps the backend error codes the accept-unconditional-offer endpoint can return onto the copy
 * shown to the customer. Codes carrying a `step` send the dialog back to that step so the customer
 * can see the field the message belongs to.
 */
const API_ERRORS = {
  'erhl-next-of-kin-matches-applicant-error': {
    step: 'nextOfKin',
    message: MOBILE_MATCHES_APPLICANT_MESSAGE,
    // The API does not say which of the two fields collided, so the same comparison is re-run
    // locally to anchor the message. If nothing matches locally (the loan changed under the
    // customer, say) both fields carry it rather than none.
    resolveFieldErrors: (nextOfKin, applicants) => {
      const matches = findApplicantContactMatches(nextOfKin, applicants);
      if (Object.keys(matches).length) return matches;
      return { mobilePhoneNumber: MOBILE_MATCHES_APPLICANT_MESSAGE, email: EMAIL_MATCHES_APPLICANT_MESSAGE };
    },
  },
  'erhl-next-of-kin-required-error': {
    step: 'nextOfKin',
    message: NEXT_OF_KIN_REQUIRED_MESSAGE,
  },
};

/**
 * Translates a failed acceptance request into customer-facing copy. The API replies with machine
 * codes such as `erhl-next-of-kin-matches-applicant-error`, and a failure inside the acceptance
 * transaction surfaces as `internal-server-error`; neither must ever reach the screen, so anything
 * unrecognised falls back to a generic message.
 *
 * @param {{ response?: { data?: { message?: string | string[] } } }} error The rejected request error
 * @param {{ nextOfKin?: Object, applicants?: Array<Object> }} [context] Used to anchor field errors
 * @returns {{ fieldErrors: Record<string, string>, message: string, step: string | null }}
 */
export function mapAcceptOfferApiError(error, context = {}) {
  const responseMessage = error?.response?.data?.message;
  const codes = Array.isArray(responseMessage) ? responseMessage : [responseMessage];
  const fieldErrors = {};
  const messages = [];
  let step = null;
  codes.forEach((code) => {
    const mapped = API_ERRORS[code];
    if (!mapped) return;
    if (mapped.resolveFieldErrors) {
      Object.assign(fieldErrors, mapped.resolveFieldErrors(context.nextOfKin, context.applicants));
    }
    if (mapped.step && !step) step = mapped.step;
    messages.push(mapped.message);
  });
  return { fieldErrors, message: messages[0] || GENERIC_API_ERROR_MESSAGE, step };
}
